import Web3 from "web3";
import cakeLpFarmingPools from "@/domain/cakeLpFarming/pools";
import cakeLps from "@/domain/cakeLpFarming/cakeLps";
import token from "@/domain/cakeLpFarming/token";
import rewardToken from "@/domain/cakeLpFarming/rewardToken";
import network from "@/domain/cakeLpFarming/network";
import bnbToken from "@/domain/cakeLpFarming/otherTokens/bnbToken";
import busdToken from "@/domain/cakeLpFarming/otherTokens/busdToken";
import { MAX_UINT256, METAMASK } from "../constants";
import pancakePair from "@/domain/cakeLpFarming/pairs/pancakePair";
import numFormat from "number-format.js";

export const getAccountData = async (web3, token, accountAddresses = []) => {
  try {
    let accounts =
      accountAddresses.length > 0
        ? accountAddresses
        : sessionStorage.getItem("provider") == METAMASK
        ? await _getAccountForMetaMask()
        : await web3.eth.getAccounts();

    let address = accounts[0];

    address = web3.utils.toChecksumAddress(address);
    let contract = new web3.eth.Contract(token.abi, token.contractAddress);

    let balance = await contract.methods
      .balanceOf(address)
      .call({ from: address });

    balance = web3.utils.toBN(balance).toString();

    let symbol = await contract.methods.symbol().call();

    return {
      address,
      balance,
      symbol,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getPools = async (web3, accountAddress) => {
  try {
    let tokenContract = new web3.eth.Contract(token.abi, token.contractAddress);

    let tokenSymbol = await tokenContract.methods.symbol().call();

    let pools = await Promise.all(
      cakeLpFarmingPools.map(async (stakingPool, index) => {
        let poolContract = new web3.eth.Contract(
          stakingPool.abi,
          stakingPool.contractAddress
        );

        let lpPool = null;

        if (stakingPool.type == "bnb") {
          lpPool = cakeLps[0];
        }

        if (stakingPool.type == "busd") {
          lpPool = cakeLps[1];
        }

        let lpPoolContract = new web3.eth.Contract(
          lpPool.abi,
          lpPool.contractAddress
        );

        let minDepositAmount = await poolContract.methods
          .minDepositAmount()
          .call();

        let totalStakingTokens = await poolContract.methods
          .totalStakingTokens()
          .call();

        let stakeTokenAddress = await poolContract.methods.stakedToken().call();
        let rewardTokenAddress = await poolContract.methods
          .rewardToken()
          .call();

        let rewardTokenContract = new web3.eth.Contract(
          rewardToken.abi,
          rewardTokenAddress
        );

        let rewardTokenSymbol = await rewardTokenContract.methods
          .symbol()
          .call();

        let rewardTokenDecimals = await rewardTokenContract.methods
          .decimals()
          .call();

        let rewardPerBlock = await poolContract.methods.rewardPerBlock().call();

        let isActive = await poolContract.methods.isInitialized().call();
        let isFrozen = await poolContract.methods.isFrozen().call();

        let apy = 3000;

        if (totalStakingTokens > 0) {
          let apr = _calculateApr(rewardPerBlock, totalStakingTokens);

          let saunaPerLp = 0;

          let lpSupply = await lpPoolContract.methods.totalSupply().call();

          let balanceOfSauna = await tokenContract.methods
            .balanceOf(lpPool.contractAddress)
            .call();

          if (balanceOfSauna == 0) {
            balanceOfSauna = 1;
          }

          if (lpSupply != 0) {
            saunaPerLp = (balanceOfSauna * 2) / lpSupply;
          }

          apy = apr / saunaPerLp;
        }

        // calculate liquidity
        let totalLiqudity = 0;

        if (stakingPool.type == "bnb") {
          let pancakePairContract = new web3.eth.Contract(
            pancakePair.abi,
            pancakePair.contractAddress
          );

          let reserves = await pancakePairContract.methods.getReserves().call();

          let bnbPrice = reserves[1] / reserves[0];

          let bnbTokenContract = new web3.eth.Contract(
            bnbToken.abi,
            bnbToken.contractAddress
          );

          let totalBnbStaked = await bnbTokenContract.methods
            .balanceOf(lpPool.contractAddress)
            .call({ from: lpPool.contractAddress });

          totalBnbStaked = _convertWeiToEther(web3, totalBnbStaked);

          totalLiqudity = totalBnbStaked * 2 * bnbPrice;
        }

        if (stakingPool.type == "busd") {
          let busdTokenContract = new web3.eth.Contract(
            busdToken.abi,
            busdToken.contractAddress
          );

          let totalBusdStaked = await busdTokenContract.methods
            .balanceOf(lpPool.contractAddress)
            .call({ from: lpPool.contractAddress });

          totalBusdStaked = _convertWeiToEther(web3, totalBusdStaked);

          totalLiqudity = totalBusdStaked * 2;
        }

        let account = {
          earned: `${0}`,
          balance: `${0}`,
          staked: `${0}`,
          isApproved: false,
        };

        if (accountAddress) {
          let accountPoolData = await poolContract.methods
            .userInfo(accountAddress)
            .call({ from: accountAddress });

          let pendingReward = await poolContract.methods
            .pendingReward(accountAddress)
            .call({ from: accountAddress });

          if (accountPoolData.registered) {
            account.isApproved = true;
            account.staked = web3.utils.toBN(accountPoolData.amount).toString();
            account.earned = web3.utils.toBN(pendingReward).toString();
          }

          if (!account.isApproved) {
            let allowance = await lpPoolContract.methods
              .allowance(accountAddress, stakingPool.contractAddress)
              .call({ from: accountAddress });

            account.isApproved = allowance != 0;
          }

          let balance = await lpPoolContract.methods
            .balanceOf(accountAddress)
            .call({ from: accountAddress });

          account.balance = web3.utils.toBN(balance).toString();
        }

        return {
          title: stakingPool.title,
          type: stakingPool.type,
          temperature: stakingPool.temperature,
          description:
            minDepositAmount > 0
              ? `Min. Stake ${numFormat(
                  "#,##0.",
                  _convertWeiToEther(web3, minDepositAmount)
                )} CAKE-LP`
              : `No minimum`,
          url: stakingPool.url,
          NETWORK: network.name,
          CHAIN_ID: network.chainIds[0],
          coinSymbol: tokenSymbol,
          mainCoinImage: null,
          smallerCoinImage: null,
          minDepositAmount: web3.utils.toBN(minDepositAmount).toString(),
          totalLiqudity: totalLiqudity,
          swapUrl: stakingPool.swapUrl,
          pairUrl: stakingPool.pairUrl,
          stakeTokenAddress: stakeTokenAddress,
          stakeTokenSymbol: lpPool.name,
          stakeTokenDecimals: lpPool.decimals,
          rewardTokenAddress: rewardTokenAddress,
          rewardTokenSymbol: rewardTokenSymbol,
          rewardTokenDecimals: rewardTokenDecimals,
          stakeContractAddress: stakingPool.contractAddress,
          status: isActive ? "ACTIVE" : "FINISHED",
          apy: apy,
          order: index + 1,
          isMaster: false,
          id: index + 1,
          createdAt: new Date(),
          updatedAt: new Date(),
          isFrozen: isFrozen,
          isFinished: isFrozen,
          account: account,
        };
      })
    );
    return pools;
  } catch (e) {
    console.error(e);
  }
};

export const approve = async (web3, lpPool, farmingPool, accountAddress) => {
  try {
    let lpPoolContract = new web3.eth.Contract(
      lpPool.abi,
      lpPool.contractAddress
    );

    let approve = await lpPoolContract.methods
      .approve(farmingPool.stakeContractAddress, MAX_UINT256)
      .send({ from: accountAddress });

    return approve;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const deposit = async (
  web3,
  farmingPool,
  accountAddress,
  depositAmount
) => {
  try {
    let amount = _convertEtherToWei(web3, depositAmount);

    let abi = cakeLpFarmingPools.find(
      (pool) => pool.contractAddress == farmingPool.stakeContractAddress
    ).abi;

    let contract = new web3.eth.Contract(abi, farmingPool.stakeContractAddress);

    let checkApproval = _checkApproval(
      web3,
      accountAddress,
      farmingPool.stakeContractAddress
    );

    if (checkApproval) {
      let desposit = await contract.methods
        .deposit(amount)
        .send({ from: accountAddress });
      return desposit;
    }
    return null;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const withdraw = async (
  web3,
  farmingPool,
  accountAddress,
  withdrawAmount
) => {
  try {
    let amount = _convertEtherToWei(web3, withdrawAmount);

    let abi = cakeLpFarmingPools.find(
      (pool) => pool.contractAddress == farmingPool.stakeContractAddress
    ).abi;

    let contract = new web3.eth.Contract(abi, farmingPool.stakeContractAddress);

    let withdraw = await contract.methods
      .withdraw(amount)
      .send({ from: accountAddress });

    return withdraw;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const harvest = async (web3, farmingPool, accountAddress) => {
  try {
    let amount = _convertEtherToWei(web3, "0");

    let abi = cakeLpFarmingPools.find(
      (pool) => pool.contractAddress == farmingPool.stakeContractAddress
    ).abi;
    let contract = new web3.eth.Contract(abi, farmingPool.stakeContractAddress);

    let harvest = await contract.methods
      .withdraw(amount)
      .send({ from: accountAddress });

    return harvest;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const addToMetamask = async (web3) => {
  let tokenContract = new web3.eth.Contract(token.abi, token.contractAddress);

  let symbol = await tokenContract.methods.symbol().call();
  let decimals = await tokenContract.methods.decimals().call();
  let image = null;

  try {
    const isAdded = await window.ethereum.request({
      method: "wallet_watchAsset",
      params: {
        type: "ERC20",
        options: {
          address: token.contractAddress,
          symbol: symbol,
          decimals: decimals,
          image: image,
        },
      },
    });

    return isAdded;
  } catch (e) {
    console.error(e);
  }
};

export const _getWeb3Provider = () => {
  try {
    const web3 = new Web3(Web3.givenProvider || "ws://localhost:8545");

    if (web3.eth.currentProvider.chainId != "0x38") {
      web3.eth.setProvider("https://bsc-dataseed.binance.org");
      return web3;
    }

    return web3;
  } catch (e) {
    console.error(e);
  }
};

export const _getContractInstance = (abi, address) => {
  let web3 = _getWeb3Provider();
  return new web3.eth.Contract(abi, address);
};

export const _getAccountForMetaMask = async () => {
  if (!window.ethereum) return;

  let accounts = [];
  await window.ethereum
    .request({
      method: "eth_requestAccounts",
    })
    .then(async (acts) => {
      accounts = acts;
    })
    .catch((error) => {
      if (error.code === 4001) {
        console.log("Permissions needed to continue.");
      } else {
        console.log(error);
      }
      accounts = [];
    });

  return accounts;
};

export const _checkApproval = async (web3, address, approveAddress) => {
  let approveContract = cakeLpFarmingPools.find(
    (pool) => pool.contractAddress == approveAddress
  );

  let lpPool = null;

  if (approveContract.type == "bnb") {
    lpPool = cakeLps[0];
  }

  if (approveContract.type == "busd") {
    lpPool = cakeLps[1];
  }

  let lpPoolContract = new web3.eth.Contract(
    lpPool.abi,
    lpPool.contractAddress
  );

  let allowance = await lpPoolContract.methods
    .allowance(address, approveAddress)
    .call({ from: address });

  if (allowance != 0) {
    return true;
  }

  let isApproved = await approve(lpPool, approveAddress, address);
  if (isApproved != null) {
    return true;
  }

  return false;
};

export const _toCurrency = (num) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
  }).format(num);

export const _convertWeiToEther = (web3, amount) =>
  web3.utils.fromWei(amount, "ether");

export const _convertEtherToWei = (web3, amount) =>
  web3.utils.toWei(amount, "ether");

export const _calculateApr = (rewardPerBlock, totalStakingTokens) => {
  return ((rewardPerBlock * 28800) / totalStakingTokens) * 100 * 365;
};
